import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaShopify, FaWordpress } from 'react-icons/fa';
import { Globe, ArrowRight, Plug } from 'lucide-react';
import { Button } from '@/components/ui/button';

const IntegrationsPreview = () => {
  const platforms = [
    { name: 'Shopify', icon: FaShopify, color: '#95BF47', primary: true, bg: 'bg-emerald-50 border-emerald-200' },
    { name: 'WooCommerce', icon: FaWordpress, color: '#7B68A6', bg: 'bg-purple-50 border-purple-200' },
    { name: 'Custom API', icon: Globe, color: '#0D9488', bg: 'bg-teal-50 border-teal-200' }
  ];

  return (
    <section className="py-16 sm:py-24 md:py-32 bg-white" data-testid="integrations-preview">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        {/* Header */}
        <motion.div 
          className="text-center mb-12 sm:mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div className="inline-flex items-center gap-2 bg-slate-100 border border-slate-200 rounded-full px-3 sm:px-4 py-2 mb-4 sm:mb-6">
            <Plug className="w-4 h-4 text-slate-600" />
            <span className="text-xs sm:text-sm font-bold text-slate-700">Platform Integration</span>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-slate-900 mb-4 sm:mb-5">
            Built for Shopify.{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-emerald-500">Works Everywhere.</span>
          </h2>
        </motion.div>

        {/* Platform Logos */}
        <div className="flex flex-wrap justify-center items-center gap-6 sm:gap-10 md:gap-16 mb-8 sm:mb-12">
          {platforms.map((platform, index) => (
            <motion.div
              key={platform.name}
              className="flex flex-col items-center gap-2 sm:gap-4"
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
            >
              <div className={`w-16 sm:w-20 md:w-24 h-16 sm:h-20 md:h-24 rounded-2xl sm:rounded-3xl ${platform.bg} border-2 flex items-center justify-center transition-transform hover:scale-110 shadow-sm`}>
                <platform.icon className="w-8 sm:w-10 md:w-12 h-8 sm:h-10 md:h-12" style={{ color: platform.color }} />
              </div>
              <span className={`text-xs sm:text-sm md:text-base font-bold ${platform.primary ? 'text-teal-600' : 'text-slate-600'}`}>
                {platform.name}
              </span>
              {platform.primary && (
                <span className="text-[10px] sm:text-xs font-bold bg-teal-100 text-teal-700 px-2 sm:px-3 py-0.5 sm:py-1 rounded-full border border-teal-200">Primary</span>
              )}
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div 
          className="text-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <Link to="/integrations">
            <Button 
              size="lg" 
              variant="outline" 
              className="group border-2 border-slate-300 text-slate-700 hover:text-teal-600 hover:border-teal-500 hover:bg-teal-50 font-semibold text-sm sm:text-base px-6 sm:px-8"
              data-testid="view-integrations-btn"
            >
              View All Integrations
              <ArrowRight className="w-4 sm:w-5 h-4 sm:h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default IntegrationsPreview;
